import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { LoadState } from '../../components/workflowShared';
import type { Workspace, WorkspaceScanSummary } from '../../components/screens/RunScreens';
import { planHasPartner, type AppPlanId } from './appPlans';
import { buildRegister, loadRegisterData, type IssueRegister, type RegisterIssue } from './useWorkspaceIssues';

/*
 * The security overview: where each application stands and the next steps Havu proposes. Havu
 * re-checks it whenever the app is opened, regains focus or an assessment changes, and every half
 * hour while it stays open. It never starts an assessment on its own; each step waits for the user.
 */

/**
 * When Havu's AI analysis last gained newer models or skills. Applications assessed before this
 * are offered a re-assessment, since their code rarely changes but the analysis does.
 */
export const ANALYSIS_UPDATED_AT = '2026-10-01';

export type OverviewActionKind =
  | 'setup'
  | 'baseline'
  | 'retry'
  | 'fix'
  | 'triage'
  | 'refresh'
  | 'allowance';

export interface OverviewApplication {
  workspace: Workspace;
  register: IssueRegister;
  hasCodebase: boolean;
  /** Newest finished assessment. */
  lastAssessment?: WorkspaceScanSummary;
  isAssessing: boolean;
  /** Unresolved findings (open or acknowledged) by severity. */
  unresolved: Record<'critical' | 'high' | 'medium' | 'low', number>;
  fixed: number;
  /** Findings dismissed as accepted risk. */
  accepted: number;
}

export interface OverviewAction {
  /** Encodes the condition behind the step, so a changed condition is a new step. */
  id: string;
  kind: OverviewActionKind;
  workspaceId?: string;
  title: string;
  detail: string;
  tone: 'critical' | 'high' | 'medium' | 'neutral';
  priority: number;
  issue?: RegisterIssue;
}

interface StoredOverview {
  reviewedAt?: string;
}

const STORAGE_PREFIX = 'havu-overview-v1';
const REVIEW_INTERVAL_MS = 30 * 60 * 1000;
// Coming back to the tab after this long triggers a fresh check.
const STALE_AFTER_MS = 10 * 60 * 1000;
const REVIEW_CONCURRENCY = 3;
// The overview only needs recent assessments; the Issues view loads the deeper history.
const REVIEW_REPORT_SCANS = 3;
const MAX_FIX_ACTIONS_PER_APP = 3;
const ACTIVE_SCAN_STATUSES = ['queued', 'pending', 'running', 'started'];
const FINISHED_SCAN_STATUSES = ['completed', 'partial'];
const UNRESOLVED = new Set(['open', 'acknowledged']);

const EMPTY_STORE: StoredOverview = {};

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

const formatShortDate = (value: string | number): string => {
  // Date-only strings are calendar days, not UTC midnights.
  const date = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);

  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const toDay = (time: number): string => {
  const date = new Date(time);

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const readStore = (key: string): StoredOverview => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? 'null') as Partial<StoredOverview> | null;

    return parsed ? { ...EMPTY_STORE, ...parsed } : EMPTY_STORE;
  } catch {
    return EMPTY_STORE;
  }
};

const writeStore = (key: string, store: StoredOverview): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(store));
  } catch {
    // Without storage the overview still works for this visit.
  }
};

/** Runs `task` over `items` with at most `limit` in flight. */
const mapLimited = async <T, R>(items: T[], limit: number, task: (item: T) => Promise<R>): Promise<R[]> => {
  const results: R[] = new Array<R>(items.length);
  let next = 0;

  const worker = async () => {
    while (next < items.length) {
      const index = next;

      next += 1;
      results[index] = await task(items[index]);
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));

  return results;
};

const toApplication = (workspace: Workspace, register: IssueRegister): OverviewApplication => {
  const unresolved = { critical: 0, high: 0, medium: 0, low: 0 };
  let fixed = 0;
  let accepted = 0;

  register.issues.forEach((issue) => {
    if (UNRESOLVED.has(issue.status) && issue.severity in unresolved) {
      unresolved[issue.severity as keyof typeof unresolved] += 1;
    } else if (issue.status === 'fixed') {
      fixed += 1;
    } else if (issue.status === 'dismissed') {
      accepted += 1;
    }
  });

  return {
    workspace,
    register,
    hasCodebase: Boolean(workspace.scope) || register.scans.length > 0,
    lastAssessment: register.scans.find((scan) => FINISHED_SCAN_STATUSES.includes(scan.status)),
    isAssessing: register.scans.some((scan) => ACTIVE_SCAN_STATUSES.includes(scan.status)),
    unresolved,
    fixed,
    accepted
  };
};

/** Havu's judgement: what needs doing next, most important first. */
export const planActions = (
  applications: OverviewApplication[],
  planId: AppPlanId,
  scansLeft: number
): OverviewAction[] => {
  const hasPartner = planHasPartner(planId);
  const actions: OverviewAction[] = [];
  let dueAssessments = 0;

  applications.forEach((app) => {
    const { workspace, register, lastAssessment } = app;
    const name = workspace.name;

    if (register.state !== 'ready') {
      return;
    }

    if (!app.hasCodebase) {
      actions.push({
        id: `setup:${workspace.id}`,
        kind: 'setup',
        workspaceId: workspace.id,
        title: `Connect ${name}'s codebase`,
        detail: 'Choose a repository or upload a ZIP so Havu can assess it.',
        tone: 'neutral',
        priority: 50
      });
      return;
    }

    const latestScan = register.scans[0];

    if (!app.isAssessing && latestScan?.status === 'failed') {
      dueAssessments += 1;
      actions.push({
        id: `retry:${latestScan.id}`,
        kind: 'retry',
        workspaceId: workspace.id,
        title: `Re-run the failed assessment of ${name}`,
        detail: 'The last assessment did not finish, so there is no report for it.',
        tone: 'high',
        priority: 80
      });
    } else if (!app.isAssessing && !lastAssessment) {
      dueAssessments += 1;
      actions.push({
        id: `baseline:${workspace.id}`,
        kind: 'baseline',
        workspaceId: workspace.id,
        title: `Run a first assessment of ${name}`,
        detail: 'Insurers, investors and customers usually ask for one.',
        tone: 'medium',
        priority: 75
      });
    } else if (
      hasPartner
      && !app.isAssessing
      && lastAssessment
      && toDay(new Date(lastAssessment.createdAt).getTime()) < ANALYSIS_UPDATED_AT
    ) {
      dueAssessments += 1;
      actions.push({
        id: `refresh:${lastAssessment.id}:${ANALYSIS_UPDATED_AT}`,
        kind: 'refresh',
        workspaceId: workspace.id,
        title: `Re-assess ${name} with Havu's latest analysis`,
        detail: `The analysis was updated on ${formatShortDate(ANALYSIS_UPDATED_AT)}; ${name} was last assessed on ${formatShortDate(lastAssessment.createdAt)}.`,
        tone: 'neutral',
        priority: 60
      });
    }

    register.issues
      .filter((issue) => UNRESOLVED.has(issue.status) && (issue.severity === 'critical' || issue.severity === 'high'))
      .slice(0, MAX_FIX_ACTIONS_PER_APP)
      .forEach((issue) => {
        actions.push({
          id: `fix:${workspace.id}:${issue.key}`,
          kind: 'fix',
          workspaceId: workspace.id,
          title: `Fix ${issue.cleanTitle}`,
          detail: `${name} · ${issue.severity === 'critical' ? 'Critical' : 'High'} severity, shows on reports you share`,
          tone: issue.severity === 'critical' ? 'critical' : 'high',
          priority: issue.severity === 'critical' ? 90 : 70,
          issue
        });
      });

    const untriaged = lastAssessment
      ? register.issues.filter((issue) => issue.status === 'open' && issue.firstSeen.id === lastAssessment.id)
      : [];

    if (untriaged.length > 0) {
      actions.push({
        id: `triage:${lastAssessment?.id ?? workspace.id}`,
        kind: 'triage',
        workspaceId: workspace.id,
        title: `Review ${plural(untriaged.length, 'new finding')} in ${name}`,
        detail: 'Mark what you will fix and what you accept as a known risk.',
        tone: 'neutral',
        priority: 40
      });
    }
  });

  if (dueAssessments > scansLeft) {
    actions.push({
      id: `allowance:${dueAssessments}:${scansLeft}`,
      kind: 'allowance',
      title: `${plural(dueAssessments, 'assessment')} needed, ${scansLeft} left this period`,
      detail: 'Start with the application outside parties ask about, or move to a plan with more assessments.',
      tone: 'neutral',
      priority: 30
    });
  }

  return actions.sort((left, right) => right.priority - left.priority);
};

interface OverviewOptions {
  /** Account id; the overview is stored per account. Empty while signed out. */
  accountId: string;
  workspaces: Workspace[];
  workspacesReady: boolean;
  planId: AppPlanId;
  scansLeft: number;
  /** Bump to check again, for example after an assessment starts or finishes. */
  reloadKey: number;
}

/** Keeps the overview for the signed-in account and re-checks it while the app is open. */
export function useSecurityOverview({ accountId, workspaces, workspacesReady, planId, scansLeft, reloadKey }: OverviewOptions) {
  const storageKey = accountId ? `${STORAGE_PREFIX}:${accountId}` : '';
  const [registers, setRegisters] = useState<Record<string, IssueRegister>>({});
  const [state, setState] = useState<LoadState>('idle');
  const reviewingRef = useRef(false);
  const workspacesRef = useRef(workspaces);
  const workspaceKey = workspaces.map((workspace) => workspace.id).join(',');

  workspacesRef.current = workspaces;

  useEffect(() => {
    setRegisters({});
    setState('idle');
  }, [storageKey]);

  const review = useCallback(async () => {
    const targets = workspacesRef.current;

    if (!storageKey || reviewingRef.current) {
      return;
    }

    reviewingRef.current = true;
    setState((current) => current === 'ready' ? current : 'loading');

    try {
      const loaded = await mapLimited(targets, REVIEW_CONCURRENCY, async (workspace) => {
        try {
          return buildRegister(await loadRegisterData(workspace.id, REVIEW_REPORT_SCANS));
        } catch (error) {
          console.error(`Failed to check ${workspace.name}:`, error);
          return buildRegister({ state: 'error', workspaceId: workspace.id, scans: [], reports: {} });
        }
      });

      setRegisters(Object.fromEntries(loaded.map((register, index) => [targets[index].id, register])));
      setState('ready');
      writeStore(storageKey, { ...readStore(storageKey), reviewedAt: new Date().toISOString() });
    } finally {
      reviewingRef.current = false;
    }
  }, [storageKey]);

  // Check on open, when the applications change and when an assessment starts or finishes.
  useEffect(() => {
    if (storageKey && workspacesReady) {
      void review();
    }
  }, [review, reloadKey, storageKey, workspaceKey, workspacesReady]);

  // While the app stays open, check periodically and when the user comes back to it.
  useEffect(() => {
    if (!storageKey || !workspacesReady) {
      return undefined;
    }

    const reviewIfStale = () => {
      const reviewedAt = readStore(storageKey).reviewedAt;

      if (document.visibilityState === 'visible'
        && (!reviewedAt || Date.now() - new Date(reviewedAt).getTime() > STALE_AFTER_MS)) {
        void review();
      }
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void review();
      }
    }, REVIEW_INTERVAL_MS);

    document.addEventListener('visibilitychange', reviewIfStale);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', reviewIfStale);
    };
  }, [review, storageKey, workspacesReady]);

  const applications = useMemo(() => workspaces
    .filter((workspace) => registers[workspace.id])
    .map((workspace) => toApplication(workspace, registers[workspace.id])), [registers, workspaces]);

  const actions = useMemo(() => planActions(applications, planId, scansLeft), [applications, planId, scansLeft]);

  return { state, applications, actions };
}
